import type { FailureOutcome, OperationFailure } from '../model/operations';

export type MutationContext = {
  baseVersion: number;
};

export type MutationLaneDeps = {
  getVersion: (templateId: string) => number;
  classifyFailure: (error: unknown) => OperationFailure;
};

export type MutationLane = {
  enqueue: <T>(
    templateId: string,
    execute: (context: MutationContext) => Promise<T>
  ) => Promise<T>;
  // Rejects the tasks that have not started. A running task cannot be cancelled
  abort: (templateId: string, cause: Error) => void;
};

type QueuedTask = {
  run: () => Promise<OperationFailure | undefined>;
  abort: (error: Error) => void;
};

export class OperationAbortedError extends Error {
  readonly outcome: FailureOutcome;

  constructor(outcome: FailureOutcome, options?: ErrorOptions) {
    super('Operation was not run because an earlier operation failed', options);
    this.name = 'OperationAbortedError';
    this.outcome = outcome;
  }
}

export class UnmountedError extends Error {
  constructor() {
    super('Operation was not run because the page was closed');
    this.name = 'UnmountedError';
  }
}

export function createMutationLane({
  getVersion,
  classifyFailure,
}: MutationLaneDeps): MutationLane {
  const queues = new Map<string, QueuedTask[]>();

  // A throwing classifier would end drain early and leave the queue stuck
  function classifySafely(error: unknown): OperationFailure {
    try {
      return classifyFailure(error);
    } catch {
      return {
        outcome: 'unknown',
        message: 'The failure could not be classified',
        cause: error,
      };
    }
  }

  async function drain(templateId: string, queue: QueuedTask[]): Promise<void> {
    let task = queue.shift();

    while (task) {
      const failure = await task.run();

      if (failure) {
        const successors = queue.splice(0);

        successors.forEach(successor => {
          const error = new OperationAbortedError(failure.outcome, {
            cause: failure.cause,
          });

          successor.abort(error);
        });
      }

      task = queue.shift();
    }

    queues.delete(templateId);
  }

  function enqueue<T>(
    templateId: string,
    execute: (context: MutationContext) => Promise<T>
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const task: QueuedTask = {
        run: async () => {
          try {
            // Read at start, so a queued write sees the version its
            // predecessor produced
            const baseVersion = getVersion(templateId);
            const result = await execute({ baseVersion });

            resolve(result);

            return undefined;
          } catch (error) {
            reject(error);

            return classifySafely(error);
          }
        },
        abort: reject,
      };

      const queue = queues.get(templateId);

      if (queue) {
        queue.push(task);

        return;
      }

      const newQueue = [task];

      queues.set(templateId, newQueue);
      drain(templateId, newQueue);
    });
  }

  function abort(templateId: string, cause: Error): void {
    const queue = queues.get(templateId) ?? [];

    queue.splice(0).forEach(task => task.abort(cause));
  }

  return { enqueue, abort };
}
